/**
 * Plan cart manager
 * Handles complex relationships between plans such as dependencies and exclusions
 */
import { cloneDeep, uniq, isArray, keyBy } from 'lodash';
import { getCartRequest, setCartRequest } from '@/apis/cart';

import { ReasonCode, BILLING_CYCLE, BillingCycleMap } from './constants';
import { getCurrency, isJestTest, safeJSONParse } from './utils';
import { Plan } from './models/Plan';
import { Reason } from './models/Reason';
import { DependencyRule } from './rules/DependencyRule';

// Cart manager
export class PlanCart {
  constructor(planMap = [], options = {}) {
    this.plans = this.#createLoggedMap(this, new Map()); // planCode -> Plan
    this.rules = []; // Rules array
    this.planMap = planMap; // Store plan metadata array
    this.addedPlans = [];
    this.removedPlans = [];
    this.updatedPlans = [];
    this.history = new Map();
    this.config = {};
    this.context = null; // Context for current operation (e.g., preferredDependencyCategory for this addPlan call)
    this.eventListeners = new Map(); // Store event listeners: event -> [callback1, callback2, ...]
    this.autoSave = options.autoSave !== undefined ? options.autoSave : true; // Whether to automatically save data after operations
    this.hooks = {
      getBasePlan: options.getBasePlan || null,
    };

    // Return a Proxy to intercept method calls and apply common logic
    return this.#createMethodProxy();
  }

  /**
   * Create a Proxy to intercept method calls and apply common logic (AOP)
   * Methods that need common logic: addPlan, removePlan, undo
   */
  #createMethodProxy() {
    // Methods that need common logic (pre-logic: history/clear, post-logic: validation/save, rollback on error)
    const methodsWithCommonLogic = new Set(['addPlan', 'removePlan', 'undo']);

    return new Proxy(this, {
      get(target, methodName) {
        const value = target[methodName];

        // If it's a method that needs common logic, wrap it
        if (methodsWithCommonLogic.has(methodName) && typeof value === 'function') {
          return async function (...args) {
            // Save initialState for rollback
            const initialState = {
              plans: cloneDeep(new Map(target.plans)),
              addedPlans: cloneDeep(target.addedPlans),
              removedPlans: cloneDeep(target.removedPlans),
              updatedPlans: cloneDeep(target.updatedPlans),
              history: cloneDeep(target.history),
            };

            // Pre-logic: save history and clear arrays
            const history = cloneDeep(new Map(target.plans));
            target.addedPlans = [];
            target.removedPlans = [];
            target.updatedPlans = [];

            // Set context from options and toAddPlans (if second argument is an options object)
            const context = {};
            if (args.length > 1) {
              const options = args[1];
              if (options?.preferredDependencyCategory) {
                context.preferredDependencyCategory = options.preferredDependencyCategory;
              }
            }
            // Store toAddPlans in context for addPlan method
            if (methodName === 'addPlan' && args.length > 0) {
              const planOrPlans = args[0];
              context.toAddPlans = Array.isArray(planOrPlans) ? planOrPlans : [planOrPlans];
            }
            target.context = context;

            try {
              // Execute the original method
              const methodResult = await value.apply(target, args);
              if (methodResult === false) {
                throw new Error('Execute error');
              }

              // set history after execution
              target.history = history;
              // Post-logic: validate and adjust license dependencies
              target.#validateAndAdjustLicenseDependencies();

              // some ZP usage plans can't exist without dependent plan
              target.#removeOrphanedUsagePlans();

              // Post-logic: save data (if result contains plans and autoSave is enabled)
              if (target.autoSave && methodResult && methodResult.plans) {
                const response = await target.#saveData(target.getAllPlans());
                if (response.result !== "success") {
                  throw new Error("save error");
                }
              }

              const result = {
                addedPlans: target.addedPlans,
                removedPlans: target.removedPlans,
                updatedPlans: target.updatedPlans,
                plans: target.getAllPlans(),
              };
              // Trigger event listeners for the method asynchronously
              target.#triggerEvent(methodName, result);

              return result;
            } catch (error) {
              // Rollback: restore initial state
              if (initialState) {
                target.plans = target.#createLoggedMap(target, initialState.plans);
                target.addedPlans = initialState.addedPlans;
                target.removedPlans = initialState.removedPlans;
                target.updatedPlans = initialState.updatedPlans;
                target.history = initialState.history;
              }

              // Re-throw the error
              throw error;
            } finally {
              // Clear context after operation
              target.context = {};
            }
          };
        }

        // For other properties/methods, return as-is
        return typeof value === 'function' ? value.bind(target) : value;
      },
    });
  }

  #createLoggedMap(cart, originalMap = new Map()) {
    return new Proxy(originalMap, {
      get(target, methodName) {
        if (methodName === 'set') {
          return function (key, value) {
            const result = target.set(key, value);
            // Check if plan already exists in addedPlans
            const existingIndex = cart.addedPlans.findIndex((plan) => plan.planCode === key);
            const clonedValue = cloneDeep(value);
            if (existingIndex >= 0) {
              // Update existing plan in addedPlans
              cart.addedPlans[existingIndex] = clonedValue;
            } else {
              // Add new plan to addedPlans
              cart.addedPlans.push(clonedValue);
            }
            delete value.reason;
            return result;
          };
        }

        if (methodName === 'delete') {
          return function (key) {
            const plan = cart.getPlanByCode(key);
            if (plan) {
              cart.removedPlans.push(cloneDeep(plan));
              delete plan.reason;
            }
            const result = target.delete(key);
            return result;
          };
        }

        // Other methods return directly
        const value = target[methodName];
        return typeof value === 'function' ? value.bind(target) : value;
      },
    });
  }

  // Add rule
  addRule(rule) {
    this.rules.push(rule);
  }

  // Automatically match billing cycle based on planMap
  #findMatchingPlanByBillingCycle(planToAdd, targetBillingCycle) {
    if (!this.planMap || this.planMap.length === 0) {
      return null;
    }

    // Find plan with same category but different billing cycle in planMap
    const matchingPlan = this.planMap.find(
      (planMeta) => planMeta.category === planToAdd.category && planMeta.billing_cycle === targetBillingCycle,
    );

    if (matchingPlan) {
      return new Plan(
        matchingPlan.plan_code,
        matchingPlan.planNameKey || matchingPlan.typeNameKey || matchingPlan.plan_code,
        matchingPlan.billing_cycle,
        matchingPlan.category,
        matchingPlan.type,
        planToAdd.selectNumber,
        matchingPlan.amount, // Keep price in cents (don't divide by 100)
        matchingPlan.capacity,
        matchingPlan.capacityUnit,
        matchingPlan.unit,
        planToAdd.extra,
      );
    }

    return null;
  }

  // Extract cart capacity fields from planMeta if they exist
  #getExtraFields(planMeta) {
    const extraFields = {};
    if (planMeta.cartCapacity !== undefined) {
      extraFields.cartCapacity = planMeta.cartCapacity;
      extraFields.cartCapacityUnit = planMeta.cartCapacityUnit;
    }
    if (planMeta.sub_type !== undefined) {
      extraFields.sub_type = planMeta.sub_type;
    }
    if (planMeta.region !== undefined) {
      extraFields.region = planMeta.region;
    }
    // Pass through pbxCategory from planMeta if it exists
    // This field is calculated in pre.js (e.g., ZONE_EDU_PLUS/pre.js)
    // by mapping region to pbxCategory via ucPbxList lookup
    if (planMeta.pbxCategory) {
      extraFields.pbxCategory = planMeta.pbxCategory;
    }
    // Pass through planName from planMeta if it exists
    // This field is used for specific product models (e.g., ZOOM_DEVICE: "HP Poly Edge E320")
    // Allows persistent cart to display product-specific subtitles
    if (planMeta.planName) {
      extraFields.planName = planMeta.planName;
    }
    return extraFields;
  }

  // Automatically add dependent plan
  #getAutoAddDependencyPlan(planToAdd) {
    // Find dependency rule
    const dependencyRule = this.rules.find(
      (rule) => rule instanceof DependencyRule && rule.dependentCategory === planToAdd.category,
    );

    if (!dependencyRule) {
      return null; // No dependency rule
    }

    // Check if dependent plan already exists
    const hasRequiredPlan = dependencyRule.requiredCategories.some((requiredCategory) =>
      this.hasCategory(requiredCategory),
    );

    if (hasRequiredPlan) {
      return null; // Dependent plan already exists
    }

    // Ensure billing cycle consistency
    const billingCycleEqualFun = (billingCycle) => {
      if (dependencyRule.billingCycleEqual) {
        // Billing cycle must be exactly equal
        return billingCycle === planToAdd.billingCycle || billingCycle === BILLING_CYCLE.PAY_AS_YOU_GO;
      } else if (dependencyRule.billingCycleMatch) {
        let targetBillingCycle = [BILLING_CYCLE.ONE_MONTH, BILLING_CYCLE.ONE_TIME].includes(planToAdd.billingCycle)
          ? [BILLING_CYCLE.ONE_MONTH, BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ONE_TIME]
          : [planToAdd.billingCycle];
        targetBillingCycle = targetBillingCycle.concat([BILLING_CYCLE.PAY_AS_YOU_GO]);
        return targetBillingCycle.includes(billingCycle);
      } else {
        return true;
      }
    };

    // Find plan matching billing cycle
    // Priority: 1. preferredDependencyCategory (if set in context), 2. first required category, 3. any required category
    let matchingDependencyPlan = null;
    const preferredDependencyCategory = this.context?.preferredDependencyCategory;

    // First, try preferredDependencyCategory if it's in requiredCategories
    if (preferredDependencyCategory && dependencyRule.requiredCategories.includes(preferredDependencyCategory)) {
      matchingDependencyPlan = this.planMap.find(
        (planMeta) => planMeta.category === preferredDependencyCategory && billingCycleEqualFun(planMeta.billing_cycle),
      );
    }

    // If not found, try first required category
    if (!matchingDependencyPlan) {
      const firstRequiredCategory = dependencyRule.requiredCategories[0];
      matchingDependencyPlan = this.planMap.find(
        (planMeta) => planMeta.category === firstRequiredCategory && billingCycleEqualFun(planMeta.billing_cycle),
      );
    }

    // If still not found, try any required category
    if (!matchingDependencyPlan) {
      matchingDependencyPlan = this.planMap.find(
        (planMeta) =>
          dependencyRule.requiredCategories.includes(planMeta.category) && billingCycleEqualFun(planMeta.billing_cycle),
      );
    }

    if (matchingDependencyPlan) {
      // Create dependent plan object
      // Include CMR-specific capacity fields if they exist
      const extra = this.#getExtraFields(matchingDependencyPlan);

      const dependencyPlan = new Plan(
        matchingDependencyPlan.plan_code,
        matchingDependencyPlan.planNameKey || matchingDependencyPlan.typeNameKey || matchingDependencyPlan.plan_code,
        matchingDependencyPlan.billing_cycle,
        matchingDependencyPlan.category,
        matchingDependencyPlan.type,
        matchingDependencyPlan.min ?? 1, // Default quantity
        matchingDependencyPlan.amount, // Keep price in cents (don't divide by 100)
        matchingDependencyPlan.capacity,
        matchingDependencyPlan.capacityUnit,
        matchingDependencyPlan.unit,
        extra,
      );

      return { dependencyPlan, dependencyRule };
    }

    return null;
  }

  #getBasePlan() {
    if (typeof this.hooks?.getBasePlan === "function") {
      return this.hooks.getBasePlan(this);
    }
    const basePlans = [];
    // Find all base plans (plans that don't depend on other plans)
    // eslint-disable-next-line no-unused-vars
    for (const [planCode, plan] of this.plans) {
      // Check if there is a dependency rule pointing to this plan, and this plan doesn't depend on other plans
      const isTopPlan = this.rules.some(
        (rule) =>
          rule instanceof DependencyRule &&
          rule.requiredCategories.includes(plan.category) &&
          rule.dependentCategory !== plan.category,
      );

      if (isTopPlan) {
        basePlans.push(plan);
      }
    }

    if (basePlans.length) {
      // Find the one with the largest billing cycle
      return basePlans.reduce((max, current) => {
        return (BillingCycleMap[current.billingCycle] || 0) < (BillingCycleMap[max.billingCycle] || 0) ? max : current;
      });
    }

    // If no true base plan is found, return the first plan
    // eslint-disable-next-line no-unused-vars
    for (const [planCode, plan] of this.plans) {
      return plan;
    }

    return null;
  }

  // todo need enhance Get billing cycle of base plan in cart
  #getBasePlanBillingCycle() {
    return this.#getBasePlan()?.billingCycle || null;
  }

  // Update billing cycle of existing plans in cart
  // Uses multiple passes to handle cascading updates (e.g., planA depends on planB, planB depends on planC)
  // eslint-disable-next-line no-unused-vars
  #updateExistingPlansBillingCycle(newPlan = null) {
    // Current base plan's billing cycle
    const basePlanBillingCycle = this.#getBasePlanBillingCycle();

    if (!basePlanBillingCycle) {
      return;
    }

    // Multiple passes until no more updates are needed
    let hasChanges = true;
    while (hasChanges) {
      hasChanges = false;
      const plansToUpdate = [];

      // Check all existing plans
      // eslint-disable-next-line no-unused-vars
      for (const [planCode, plan] of this.plans) {
        // Get the plan with the maximum cycle that this plan depends on
        const maxBillingCyclePlan = this.#getDependencyMaxCyclePlan(plan);
        if (maxBillingCyclePlan && maxBillingCyclePlan.billingCycle !== BILLING_CYCLE.PAY_AS_YOU_GO) {
          // Priority: billingCycleEqual > billingCycleMatch
          const dependencyRule =
            this.rules.find(
              (rule) =>
                rule instanceof DependencyRule &&
                rule.dependentCategory === plan.category &&
                rule.requiredCategories.includes(maxBillingCyclePlan.category) &&
                rule.billingCycleEqual === true,
            ) ||
            this.rules.find(
              (rule) =>
                rule instanceof DependencyRule &&
                rule.dependentCategory === plan.category &&
                rule.requiredCategories.includes(maxBillingCyclePlan.category),
            );

          let shouldUpdate = false;
          if (dependencyRule?.billingCycleEqual) {
            // For billingCycleEqual, check if cycles are not equal
            shouldUpdate = plan.billingCycle !== maxBillingCyclePlan.billingCycle;
          } else if (dependencyRule?.billingCycleMatch) {
            // For billingCycleMatch, check if current cycle is greater than dependent cycle
            shouldUpdate = BillingCycleMap[plan.billingCycle] > BillingCycleMap[maxBillingCyclePlan.billingCycle];
          }

          if (shouldUpdate) {
            const matchingPlan = this.#findMatchingPlanByBillingCycle(plan, basePlanBillingCycle);
            if (matchingPlan) {
              const reason = new Reason(
                ReasonCode.DEPENDENCY_UPDATE_CYCLE,
                dependencyRule,
                `${plan.planCode} update to ${matchingPlan.planCode} to align billing cycle`,
                maxBillingCyclePlan.planCode,
              );
              plan.reason = reason;
              matchingPlan.reason = reason;
              plansToUpdate.push({ oldPlan: plan, newPlan: matchingPlan });
            }
          }
        }
      }

      // Execute update
      if (plansToUpdate.length > 0) {
        hasChanges = true;
        plansToUpdate.forEach(({ oldPlan, newPlan }) => {
          this.plans.delete(oldPlan.planCode);
          this.plans.set(newPlan.planCode, newPlan);
        });
      }
    }
  }

  #getDependencyPlans(plan) {
    // Check if automatic billing cycle matching is needed (only for addon plans)
    const dependencyCategories = this.rules
      .filter(
        (rule) =>
          rule instanceof DependencyRule &&
          rule.dependentCategory === plan.category &&
          (rule.billingCycleMatch === true || rule.billingCycleEqual === true),
      )
      .map((rule) => rule.requiredCategories)
      .flat();
    // Find all dependent plans of this addon plan in this.plans
    return Array.from(this.plans.values()).filter((plan) => dependencyCategories.includes(plan.category));
  }

  #getDependencyMaxCyclePlan(plan) {
    const dependencyPlans = this.#getDependencyPlans(plan);
    if (!dependencyPlans.length) {
      return null;
    }

    // If the base plan is in dependencyPlans, return it
    const basePlan = this.#getBasePlan();
    if (dependencyPlans.some((p) => p.planCode === basePlan.planCode)) {
      return basePlan;
    }
    // Find the one with the largest billing cycle in dependencyPlans
    return dependencyPlans.reduce((max, current) => {
      return BillingCycleMap[current.billingCycle] < BillingCycleMap[max.billingCycle] ? max : current;
    }, {});
  }

  async #saveData(plans) {
    const persistPlans = plans.map((p) => {
      return {
        planCode: p.planCode,
        selectNumber: p.selectNumber,
        extra: p.extra,
      };
    });

    const persist = keyBy(persistPlans, "planCode");
    const currency = getCurrency();
    if (!isJestTest()) {
      return await setCartRequest(JSON.stringify({ persist, currency }));
    } else {
      return {
        result: "success",
      };
    }
  }

  /**
   * Validate and automatically adjust license quantities based on dependency rules
   * Logic:
   * 1. licenseMatch: Base plans' selectNumber sum must be >= Addon plan's selectNumber
   * 2. licenseEqual: Addon plan's selectNumber must be == Base plans' selectNumber sum
   * Updated plans are added to this.updatedPlans
   * Uses this.history to determine if a plan was newly added (not in history) or already existed (in history)
   * Uses multiple passes to handle cascading adjustments (e.g., planA depends on planB, planB depends on planC)
   */
  #validateAndAdjustLicenseDependencies() {
    // 1. Process base plans first: make base plans catch up to addon requirements (for both Match and Equal)
    this.#syncBaseLicensesToMatchAddons();

    // 2. Then process addon plans: make addons adapt to base plan totals (for Equal, or for Match if base at max)
    this.#syncAddonLicensesToMatchBase();
  }

  /**
   * Synchronize Base plans to match Addon requirements
   * (Base plan follows Addon)
   * Triggered by: licenseMatch (if Base < Addon) or licenseEqual (if Base < Addon)
   */
  #syncBaseLicensesToMatchAddons() {
    const dependencyRules = this.rules.filter(
      (rule) => rule instanceof DependencyRule && (rule.licenseMatch || rule.licenseEqual),
    );

    if (dependencyRules.length === 0) {
      return;
    }

    let hasChanges = true;
    while (hasChanges) {
      hasChanges = false;
      const allPlans = this.getAllPlans();
      const adjustmentsNeeded = new Map();

      for (const plan of allPlans) {
        const applicableRules = dependencyRules.filter((rule) => rule.dependentCategory === plan.category);

        for (const rule of applicableRules) {
          const targetRequiredPlans = this.getPlansByCategories(rule.requiredCategories);
          if (targetRequiredPlans.length === 0) continue;

          const currentSelectNumber = plan.selectNumber ?? 1;
          const totalRequiredSelectNumber = targetRequiredPlans.reduce(
            (sum, requiredPlan) => sum + (requiredPlan.selectNumber ?? 1),
            0,
          );

          // Both Match and Equal rules should try to increase base if base total is insufficient
          if (totalRequiredSelectNumber < currentSelectNumber) {
            const difference = currentSelectNumber - totalRequiredSelectNumber;
            const firstRequiredPlan = targetRequiredPlans.find((rp) => {
              const rpMeta = this.planMap.find((m) => m.plan_code === rp.planCode);
              return !rpMeta || rpMeta.max === undefined || rpMeta.max === null || (rp.selectNumber ?? 1) < rpMeta.max;
            });

            if (firstRequiredPlan) {
              const existingAdjustment = adjustmentsNeeded.get(firstRequiredPlan.planCode);
              const currentFirstPlanNumber = firstRequiredPlan.selectNumber ?? 1;
              const newRequiredNumber = Math.max(
                existingAdjustment?.requiredSelectNumber || currentFirstPlanNumber,
                currentFirstPlanNumber + difference,
              );

              adjustmentsNeeded.set(firstRequiredPlan.planCode, {
                requiredSelectNumber: newRequiredNumber,
                rule: rule,
              });
            }
          }
        }
      }

      if (this.#applyLicenseAdjustments(adjustmentsNeeded)) {
        hasChanges = true;
      }
    }
  }

  /**
   * Synchronize Addon plans to match Base plan totals
   * (Addon follows Base)
   * Triggered by: licenseEqual (always) or licenseMatch (if Addon > Base total)
   */
  #syncAddonLicensesToMatchBase() {
    const dependencyRules = this.rules.filter(
      (rule) => rule instanceof DependencyRule && (rule.licenseMatch || rule.licenseEqual),
    );

    if (dependencyRules.length === 0) {
      return;
    }

    let hasChanges = true;
    while (hasChanges) {
      hasChanges = false;
      const allPlans = this.getAllPlans();
      const adjustmentsNeeded = new Map();

      for (const plan of allPlans) {
        const applicableRules = dependencyRules.filter((rule) => rule.dependentCategory === plan.category);

        for (const rule of applicableRules) {
          let targetRequiredPlans = this.getPlansByCategories(rule.requiredCategories);
          if (typeof rule.licenseEqual === "function") {
            targetRequiredPlans = rule.licenseEqual(this);
          }

          if (targetRequiredPlans.length === 0 && typeof rule.licenseEqual !== "function") continue;

          const currentSelectNumber = plan.selectNumber ?? 1;
          const totalRequiredSelectNumber = targetRequiredPlans.reduce(
            (sum, requiredPlan) => sum + (requiredPlan.selectNumber ?? 1),
            0,
          );

          let shouldAdjust = false;
          let finalTarget = currentSelectNumber;

          if (rule.licenseEqual) {
            // For Equal: any difference must be resolved
            shouldAdjust = currentSelectNumber !== totalRequiredSelectNumber;
            finalTarget = totalRequiredSelectNumber;
          } else if (rule.licenseMatch) {
            // For Match: only adjust addon DOWN if base total is insufficient (meaning base hit its max in the previous step)
            shouldAdjust = currentSelectNumber > totalRequiredSelectNumber;
            finalTarget = totalRequiredSelectNumber;
          }

          if (shouldAdjust) {
            adjustmentsNeeded.set(plan.planCode, {
              requiredSelectNumber: finalTarget,
              rule: rule,
            });
          }
        }
      }

      if (this.#applyLicenseAdjustments(adjustmentsNeeded)) {
        hasChanges = true;
      }
    }
  }

  /**
   * Internal helper to apply identified adjustments and update logs
   * @private
   */
  #applyLicenseAdjustments(adjustmentsNeeded) {
    let hasAppliedChanges = false;

    for (const [planCode, adjustment] of adjustmentsNeeded.entries()) {
      const { requiredSelectNumber, rule } = adjustment;
      const planToUpdate = this.getPlanByCode(planCode);

      if (!planToUpdate) continue;

      const planMeta = this.planMap.find((m) => m.plan_code === planCode);
      const finalRequiredNumber =
        planMeta && planMeta.max !== undefined && planMeta.max !== null
          ? Math.min(requiredSelectNumber, planMeta.max)
          : requiredSelectNumber;

      const oldSelectNumber = planToUpdate.selectNumber ?? 1;

      if (oldSelectNumber !== finalRequiredNumber) {
        planToUpdate.selectNumber = finalRequiredNumber;
        hasAppliedChanges = true;

        const isNewlyAdded = !this.history.has(planCode);
        if (!isNewlyAdded) {
          const existingIndex = this.updatedPlans.findIndex((p) => p.planCode === planCode);
          planToUpdate.reason = new Reason(
            ReasonCode.DEPENDENCY_LICENSE_ADJUST,
            rule,
            `License quantity adjusted from ${oldSelectNumber} to ${finalRequiredNumber}`,
          );

          if (existingIndex >= 0) {
            this.updatedPlans[existingIndex] = cloneDeep(planToUpdate);
          } else {
            this.updatedPlans.push(cloneDeep(planToUpdate));
          }
        } else {
          const existingAddedPlan = this.addedPlans.find((p) => p.planCode === planCode);
          if (existingAddedPlan) {
            existingAddedPlan.selectNumber = finalRequiredNumber;
          }
        }
      }
    }

    return hasAppliedChanges;
  }

  async undo() {
    if (this.history.size <= 0) {
      return false;
    }
    // Restore plans from history and recreate the Proxy wrapper
    this.plans = this.#createLoggedMap(this, cloneDeep(this.history));
    this.history.clear();
    return { addedPlans: [], removedPlans: [], updatedPlans: [], plans: this.getAllPlans() };
  }

  // Add plan(s) to cart - supports single plan or array of plans
  // Transactional: either all plans are added successfully, or all changes are rolled back
  // Note: This method is intercepted by Proxy to apply common logic (pre-logic: history/clear/initialState, post-logic: validation/save, rollback on error)

  // eslint-disable-next-line
  async addPlan(planOrPlans, options = {}) {
    // Normalize input to array
    const plansToAdd = Array.isArray(planOrPlans) ? planOrPlans : [planOrPlans];

    // Use Set to track planCodes to avoid duplicates
    const processedPlanCodes = new Set();

    // Process each plan: validate and add recursively
    for (const plan of plansToAdd) {
      const validatedPlan = this.#buildPlan(plan);

      // Skip if already processed (avoid duplicate adds)
      if (processedPlanCodes.has(validatedPlan.planCode)) {
        continue;
      }

      // Add plan recursively (handles dependencies, conflicts, etc.)
      // If any error occurs, it will be thrown and Proxy will handle rollback
      this.#addPlanRecursively(validatedPlan, true);
      processedPlanCodes.add(validatedPlan.planCode);
    }

    // Return logs (saveData will be handled by proxy method)
    return {
      addedPlans: this.addedPlans,
      removedPlans: this.removedPlans,
      updatedPlans: this.updatedPlans,
      plans: this.getAllPlans(),
    };
  }

  #buildPlan(plan) {
    let matchingPlan = null;
    if (plan.planCode) {
      matchingPlan = this.planMap.find((planMeta) => planMeta.plan_code === plan.planCode);
    } else if (plan.billingCycle && plan.category) {
      matchingPlan = this.planMap.find(
        (planMeta) =>
          planMeta.category?.toLowerCase() === plan.category?.toLowerCase() &&
          planMeta.billing_cycle === plan.billingCycle,
      );
    }
    if (!matchingPlan) {
      throw new Error('plan is invalid');
    }

    // Merge plan.extra with CMR-specific fields from matchingPlan
    const mergedExtra = {
      ...plan.extra,
      // Include CMR-specific capacity fields if they exist
      ...this.#getExtraFields(matchingPlan),
    };

    let selectNumber = plan.selectNumber ?? 1;
    selectNumber =
      selectNumber < matchingPlan.min
        ? matchingPlan.min
        : selectNumber > matchingPlan.max
        ? matchingPlan.max
        : selectNumber;

    plan = new Plan(
      matchingPlan.plan_code,
      matchingPlan.planNameKey || matchingPlan.typeNameKey || matchingPlan.plan_code,
      matchingPlan.billing_cycle,
      matchingPlan.category,
      matchingPlan.type,
      selectNumber,
      matchingPlan.amount,
      matchingPlan.capacity,
      matchingPlan.capacityUnit,
      matchingPlan.unit,
      mergedExtra,
    );
    return plan;
  }

  /**
   * Recursively call addPlan
   * Principle 1: Ensure plan can be added. If it's an addon plan, check if there's a dependent base plan in cart, if not auto add; if it's a base plan, automatically remove existing mutually exclusive other base plans
   * Principle 2: After adding new plan, ensure cycle validity. For example, base monthly + addon annual, need to change addon to monthly; if pro annual, addon annual + biz monthly, need to change addon to monthly
   */
  #addPlanRecursively(plan, isFirst = false) {
    // Check if automatic addition of dependent plan is needed
    const autoAddDependencyPlan = this.#getAutoAddDependencyPlan(plan);
    if (autoAddDependencyPlan) {
      const { dependencyPlan, dependencyRule } = autoAddDependencyPlan;
      // Add dependent plan first
      dependencyPlan.reason = new Reason(
        ReasonCode.DEPENDENCY_AUTO_ADD,
        dependencyRule,
        `Auto-added dependency plan: ${dependencyPlan.planCode}`,
        plan.planCode,
      );
      this.#addPlanRecursively(dependencyPlan);
    }

    const maxBillingCyclePlan = this.#getDependencyMaxCyclePlan(plan);
    if (
      maxBillingCyclePlan &&
      maxBillingCyclePlan.billingCycle !== BILLING_CYCLE.PAY_AS_YOU_GO &&
      !plan.extra.bundleCode
    ) {
      // Get dependency rule to check billingCycleEqual
      // Priority: billingCycleEqual > billingCycleMatch
      const dependencyRule =
        this.rules.find(
          (rule) =>
            rule instanceof DependencyRule &&
            rule.dependentCategory === plan.category &&
            rule.requiredCategories.includes(maxBillingCyclePlan.category) &&
            rule.billingCycleEqual === true,
        ) ||
        this.rules.find(
          (rule) =>
            rule instanceof DependencyRule &&
            rule.dependentCategory === plan.category &&
            rule.requiredCategories.includes(maxBillingCyclePlan.category),
        );

      let shouldMatch = false;
      if (dependencyRule?.billingCycleEqual) {
        // For billingCycleEqual, check if cycles are not equal
        shouldMatch = maxBillingCyclePlan.billingCycle !== plan.billingCycle;
      } else if (dependencyRule?.billingCycleMatch) {
        // For billingCycleMatch, check if dependent cycle is less than current cycle
        shouldMatch = BillingCycleMap[maxBillingCyclePlan?.billingCycle] < BillingCycleMap[plan.billingCycle];
      }

      if (shouldMatch) {
        // Try to find matching plan
        const matchingPlan = this.#findMatchingPlanByBillingCycle(plan, maxBillingCyclePlan.billingCycle);
        if (matchingPlan) {
          plan = matchingPlan; // Use matching plan
          if (isFirst) {
            plan.reason = new Reason(ReasonCode.USER_ADD_MATCH_CYCLE);
          }
        }
      }
    }

    // Collect plans that need cascade removal
    const planCodesToRemove = new Set();

    // Validate all rules
    for (const rule of this.rules) {
      const validation = rule.validate(this, plan);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Check if cascade removal is needed
      if (validation.cascadeRemoval) {
        // Reason and triggerBy are already set on each plan in validate method
        validation.cascadeRemoval.conflictingPlans.forEach((plan) => {
          planCodesToRemove.add(plan.planCode);
        });
      }
    }

    // Execute cascade removal
    if (planCodesToRemove.size > 0) {
      this.#performCascadeRemoval(Array.from(planCodesToRemove), plan.category);
    }

    // Add new plan
    if (isFirst && !plan.reason) {
      plan.reason = new Reason(ReasonCode.USER_ADD);
    }
    this.plans.set(plan.planCode, plan);

    // After adding new plan, update billing cycle of existing plans, e.g. pro annual, large annual + biz monthly = biz monthly + large monthly
    this.#updateExistingPlansBillingCycle(plan);
  }

  // Remove plan(s) - supports single plan or array of plans
  // Note: This method is intercepted by Proxy to apply common logic (pre-logic: history/clear, post-logic: validation/save)
  async removePlan(planOrPlans, confirmAsync) {
    // Normalize input to array
    const plansToRemove = Array.isArray(planOrPlans) ? planOrPlans : [planOrPlans];

    // If confirmFun is provided, simulate the removal first to collect data
    if (typeof confirmAsync === 'function') {
      // Save current state
      const savedPlans = cloneDeep(new Map(this.plans));
      const savedAddedPlans = cloneDeep(this.addedPlans);
      const savedRemovedPlans = cloneDeep(this.removedPlans);
      const savedUpdatedPlans = cloneDeep(this.updatedPlans);

      // Simulate removal to collect data
      this.#executeRemovePlan(plansToRemove);

      // Collect the result data
      const previewData = {
        addedPlans: this.addedPlans,
        removedPlans: this.removedPlans,
        updatedPlans: this.updatedPlans,
        plans: this.getAllPlans(),
      };

      // Call confirmFun with preview data
      const confirmed = await confirmAsync(previewData);

      // If not confirmed, return false
      if (confirmed !== true) {
        return false;
      }

      // Restore state
      this.plans = savedPlans;
      this.addedPlans = savedAddedPlans;
      this.removedPlans = savedRemovedPlans;
      this.updatedPlans = savedUpdatedPlans;
    }

    // Perform actual removal
    this.#executeRemovePlan(plansToRemove);

    // Return logs (saveData will be handled by proxy method)
    return {
      addedPlans: this.addedPlans,
      removedPlans: this.removedPlans,
      updatedPlans: this.updatedPlans,
      plans: this.getAllPlans(),
    };
  }

  // Execute removal logic (shared by both simulation and actual removal)
  #executeRemovePlan(plansToRemove) {
    // Use Set to track planCodes to avoid duplicates
    const toRemovePlansMap = new Map(); // planCode -> Plan
    const userRemovePlans = []; // Plans directly requested by user

    // Process each plan: validate, find in cart, and collect bundleCode-related plans
    for (const plan of plansToRemove) {
      try {
        const matchPlan = this.#buildPlan(plan);
        const toRemovePlan = this.getPlanByCode(matchPlan.planCode);

        if (toRemovePlan && !toRemovePlansMap.has(toRemovePlan.planCode)) {
          toRemovePlansMap.set(toRemovePlan.planCode, toRemovePlan);
          userRemovePlans.push(toRemovePlan);
          toRemovePlan.reason = new Reason(ReasonCode.USER_REMOVE);
        }
      } catch (error) {
        // Skip invalid plans, continue with others
        console.warn(`Failed to remove plan:`, plan, error);
      }
    }

    // Collect all bundleCodes from user-requested plans
    const bundleCodes = uniq(userRemovePlans.map((plan) => plan.extra?.bundleCode).filter((bundleCode) => bundleCode));

    // Find and add all plans with the same bundleCodes (excluding already collected plans)
    if (bundleCodes.length > 0) {
      const existingPlans = Array.from(toRemovePlansMap.values());
      const bundleCodePlans = this.getPlansByBundleCodes(bundleCodes, existingPlans);

      for (const plan of bundleCodePlans) {
        if (!toRemovePlansMap.has(plan.planCode)) {
          toRemovePlansMap.set(plan.planCode, plan);
          const triggerByPlan = existingPlans.find((p) => p.extra?.bundleCode === plan.extra?.bundleCode);
          plan.reason = new Reason(ReasonCode.BUNDLE_REMOVE, null, '', triggerByPlan.planCode);
        }
      }
    }

    // Perform cascade removal if there are plans to remove
    if (toRemovePlansMap.size > 0) {
      const planCodesToRemove = Array.from(toRemovePlansMap.keys());
      this.#performCascadeRemoval(planCodesToRemove);
    }
  }

  // Execute cascade removal
  #performCascadeRemoval(plansToRemove, newPlanCategory = null) {
    const removedPlans = [];
    const toRemove = new Set(plansToRemove);

    // First round: Remove directly conflicting plans
    plansToRemove.forEach((planCode) => {
      if (this.plans.has(planCode)) {
        this.plans.delete(planCode);
        removedPlans.push(planCode);
        toRemove.delete(planCode);
      }
    });

    // Second round: Find and remove other plans that depend on these plans
    let hasChanges = true;
    while (hasChanges) {
      hasChanges = false;
      const currentPlans = Array.from(this.plans.values());

      for (const plan of currentPlans) {
        // Check if this plan depends on removed plans
        const dependencyRule = this.rules.find(
          (rule) => rule instanceof DependencyRule && rule.dependentCategory === plan.category,
        );

        if (dependencyRule) {
          // Check if there is a valid dependency (including newly added plan)
          const hasValidDependency = dependencyRule.requiredCategories.some((requiredCategory) => {
            // Check if there are still plans in this category that exist and haven't been removed
            const existingPlansInCategory = this.getPlansByCategories([requiredCategory]);
            const hasExistingValidPlan = existingPlansInCategory.some((p) => !removedPlans.includes(p.planCode));

            // Check if newly added plan belongs to this category (new plan info needs to be passed from outside)
            const newPlanMatchesCategory = newPlanCategory === requiredCategory;

            return hasExistingValidPlan || newPlanMatchesCategory;
          });

          if (!hasValidDependency) {
            // All dependencies of this plan have been removed, need to remove it
            plan.reason = new Reason(
              ReasonCode.DEPENDENCY_CASCADE_REMOVE,
              dependencyRule,
              `Cascade removed due to dependency: ${dependencyRule.requiredCategories}`,
            );
            this.plans.delete(plan.planCode);
            removedPlans.push(plan.planCode);
            hasChanges = true;
            break; // Break inner loop, restart check
          }
        }
      }
    }
  }

  // Remove PAY_AS_YOU_GO plans that have no dependent plans
  #removeOrphanedUsagePlans() {
    const allPlans = Array.from(this.plans.values());
    const usagePlans = allPlans.filter((plan) => ['ZVP', 'ZVC'].includes(plan.category));

    if (usagePlans.length === 0) {
      return;
    }

    // For each PAY_AS_YOU_GO plan, check if any other plan depends on it
    for (const usagePlan of usagePlans) {
      // Check if there are any dependency rules that require this plan's category
      const hasDependentPlans = this.rules.some((rule) => {
        if (!(rule instanceof DependencyRule)) {
          return false;
        }

        // Check if this rule requires the PAY_AS_YOU_GO plan's category
        if (!rule.requiredCategories.includes(usagePlan.category)) {
          return false;
        }

        // Check if there are any plans in cart that depend on this category
        const dependentPlans = this.getPlansByCategories([rule.dependentCategory]);
        // Exclude the PAY_AS_YOU_GO plan itself
        const otherDependentPlans = dependentPlans.filter((p) => p.planCode !== usagePlan.planCode);

        return otherDependentPlans.length > 0;
      });

      // If no dependent plans exist, remove this PAY_AS_YOU_GO plan
      if (!hasDependentPlans) {
        usagePlan.reason = new Reason(
          ReasonCode.DEPENDENCY_CASCADE_REMOVE,
          null,
          `Usage plan ${usagePlan.planCode} removed: no dependent plans exist`,
        );
        this.plans.delete(usagePlan.planCode);
      }
    }
  }

  // Get plan by planCode
  getPlanByCode(planCode) {
    return this.plans.get(planCode);
  }

  // Check if there is a plan in a category
  hasCategory(category) {
    return Array.from(this.plans.values()).some((plan) => plan.category === category);
  }

  // Get plans by categories
  getPlansByCategories(categories) {
    return Array.from(this.plans.values()).filter((plan) => categories.includes(plan.category));
  }

  // Get plans by categories
  getPlansByFilter(filterFun) {
    if (typeof filterFun === 'function') {
      return Array.from(this.plans.values()).filter((plan) => filterFun(plan));
    }
    return [];
  }

  // Get all plans
  getAllPlans() {
    return Array.from(this.plans.values());
  }

  /**
   * Get plan metadata from planMap and return as Plan instance
   * @param {Object|Array} planOrPlans - Plan(s) to query (same format as addPlan method)
   * @returns {Plan|Array<Plan>|null} - Plan instance(s), or null if not found
   */
  getPlanMeta(planOrPlans) {
    const plansArray = Array.isArray(planOrPlans) ? planOrPlans : [planOrPlans];
    const results = plansArray.map(plan => {
      try {
        // Reuse #buildPlan to create Plan instance from planMap
        return this.#buildPlan(plan);
      } catch (error) {
        // Return null if plan not found in planMap
        return null;
      }
    });
    
    return Array.isArray(planOrPlans) ? results : results[0];
  }

  // Get plans by bundleCodes, excluding specified plans
  getPlansByBundleCodes(bundleCodes, excludePlans = []) {
    if (!bundleCodes || bundleCodes.length === 0) {
      return [];
    }
    const allPlans = this.getAllPlans();
    return allPlans.filter((plan) => bundleCodes.includes(plan.extra?.bundleCode) && !excludePlans.includes(plan));
  }

  // Clear cart
  clear() {
    this.plans.clear();
    if (this.autoSave) {
      this.#saveData([]);
    }
  }

  // Get cart total price
  #getTotalPrice() {
    return Array.from(this.plans.values()).reduce((total, plan) => total + plan.price, 0);
  }

  // Get cart summary
  getSummary() {
    const plans = this.getAllPlans();
    return {
      plans,
      totalPrice: this.#getTotalPrice(),
      count: plans.length,
    };
  }

  async loadData(data) {
    const res = data ?? (await getCartRequest());
    const cartInfoStr = res?.result?.cartInfo;
    const cartInfo = cartInfoStr ? safeJSONParse(cartInfoStr) : null;
    const currency = cartInfo?.currency;
    if (currency && currency !== getCurrency()) {
      this.clear();
      return;
    }
    const persist = cartInfo?.persist;
    if (!persist) {
      return;
    }
    const plans = Object.keys(persist).map((planCode) => {
      return { ...persist[planCode], planCode };
    });
    if (isArray(plans)) {
      plans.forEach((p) => {
        try {
          const matchPlan = this.#buildPlan(p);
          if (matchPlan) {
            matchPlan.selectNumber = p.selectNumber ?? (p.selected_number ? p.selected_number : 1);
            matchPlan.extra = Object.assign({}, matchPlan.extra ?? {}, p.extra ?? {});
            this.plans.set(p.planCode, matchPlan);
          }
        } catch (e) {
          console.error(e);
        }
      });
      let hasChanged = true;
      while (hasChanged) {
        hasChanged = false;
        const currentPlans = Array.from(this.plans.values());
        for (const plan of currentPlans) {
          if (!this.plans.has(plan.planCode)) {
            continue;
          }
          const notValid = this.rules
            .filter((r) => r instanceof DependencyRule)
            .some((rule) => rule.validate(this, plan)?.valid === false);
          if (notValid) {
            this.plans.delete(plan.planCode);
            hasChanged = true;
          }
        }
      }

      // Auto-validate license dependencies using AOP wrapper
      // Set history to current plans so all loaded plans are considered as existing (not newly added)
      this.history = cloneDeep(new Map(this.plans));
      this.#validateAndAdjustLicenseDependencies();
    }
  }

  /**
   * Add event listener for cart events
   * @param {string} event - Event name (e.g., 'undo')
   * @param {Function} callback - Callback function to be called when event is triggered
   * @param {*} callback.result - Result from the method that triggered the event
   */
  addEventListener(event, callback) {
    if (typeof callback !== 'function') {
      throw new Error('EventListener callback must be a function');
    }

    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }

    this.eventListeners.get(event).push(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function to remove
   */
  removeEventListener(event, callback) {
    if (!this.eventListeners.has(event)) {
      return;
    }

    const callbacks = this.eventListeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }

    if (callbacks.length === 0) {
      this.eventListeners.delete(event);
    }
  }

  /**
   * Trigger event and call all registered listeners asynchronously
   * @param {string} event - Event name
   * @param {*} data - Data to pass to listeners
   * @private
   */
  #triggerEvent(event, data) {
    if (!this.eventListeners.has(event)) {
      return;
    }

    const callbacks = this.eventListeners.get(event);
    // Execute callbacks asynchronously to avoid blocking main logic
    callbacks.forEach((callback) => {
      // Use queueMicrotask for faster execution, or setTimeout for next tick
      queueMicrotask(() => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    });
  }
}
